import {
    useDynamicBriefQuery,
    StructuredBriefFragment,
} from '@99designs/apps/spa/pages/create/briefs/DynamicBrief/DynamicBrief.generated';
import { Field } from '@99designs/brief';
import { RequestQuoteBriefWizard } from '@99designs/apps/spa/pages/create/briefs/DynamicBrief/RequestQuoteBriefWizard';
import React from 'react';
import { useParams } from 'react-router-dom';
import { PurchaseBundleBriefWizard } from './PurchaseBundleBriefWizard';
import { BundledRequestQuoteBriefWizard } from './BundledRequestQuoteBriefWizard';
import { PurchaseProductBriefWizard } from './PurchaseProductBriefWizard';
import { PartnerContext, partnerFromCategory, useSite } from '@99designs/spa-common';
import PaymentsProvider from '@99designs/apps/spa/common/Payments';
import { BriefParams } from '../Brief';

function DynamicBrief() {
    const site = useSite();

    // TODO: Add unit test for this component
    const params = useParams<BriefParams>() as BriefParams;
    const { data, loading, error, refetch } = useDynamicBriefQuery({
        variables: {
            id: params.briefId,
        },
    });

    if (loading || !data) {
        return null;
    }

    if (error) {
        throw error;
    }

    const brief = data.brief;
    const [color, partner] = partnerFromCategory(brief.category?.key);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formData = data.brief.dynamicFields.reduce<Record<string, any>>(
        (acc, field) => {
            acc[field.id] = Field.getValue(field);
            return acc;
        },
        { briefId: data.brief.id }
    );

    return (
        <PaymentsProvider locale={site.siteConfig.locale}>
            <PartnerContext.Provider value={{ color, partner }}>
                <Wizard
                    brief={brief}
                    formData={formData}
                    refetch={refetch}
                    currentStep={parseInt(params.step)}
                />
            </PartnerContext.Provider>
        </PaymentsProvider>
    );
}

/**
 * Configure wizard/steps based on the following:
 *
 * IF bundle (i.e. Logo + Website)
 *  -> render: custom payment step
 * IF package:
 *  -> render: summary, payment step
 * IF paid for "bundled" project
 *  -> render: summary, designer selection (single designer selection)
 * IF non-package (request quotes)
 *  -> render: summary, designer selection (multi-designer selection)
 */
function Wizard(props: {
    brief: StructuredBriefFragment;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formData: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refetch: (options: { id: string }) => Promise<any>;
    currentStep: number;
}) {
    const { brief } = props;

    if (brief.isBundled) {
        return <PurchaseBundleBriefWizard {...props} />;
    } else if (brief.product && !brief.order) {
        return <PurchaseProductBriefWizard {...props} />;
    } else if (brief.product && brief.order) {
        return <BundledRequestQuoteBriefWizard {...props} />;
    } else {
        return <RequestQuoteBriefWizard {...props} />;
    }
}

export default DynamicBrief;
